import React, {FC, ReactNode} from 'react';

import {HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {SimpleDropdown} from '../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown';
import {SimpleDropdownItem} from '../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdownItem';
import {PopoverTransferProps} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {flexCenter} from '../../../../../libs/shared/ui/src/styles/mixins';
import {PaletteColorsEnum} from '../../../../../libs/shared/ui/src/styles/paletteModel';
import {UserTheme} from '../../../../../libs/shared/ui/src/theme/themeTypes';
import {styled, useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {FilterDropdownBox} from '../../../../components/common/filters/filterDropdownBox/filterDropdownBox';
import {
  AnalyticsNumericDataPointType,
  AnalyticsScalarMetricsEnum,
  AnalyticsTableMetricsEnum,
} from '../../../../models/api/analyticsMetricsModel';
import {getMetricLabels, maybeRenderMetricPillAndTooltip} from '../../../helpers/metricLabels';
import {AnalyticsComputedGraphMetricsEnum} from '../../../transformers/analyticsMetricsTransformersTypes';

/*
 * Types.
 */

export interface HistoMetricSelection {
  readonly donutHistoMetricId: AnalyticsComputedGraphMetricsEnum; // Graph metric to display in the donut.
  readonly listHistoMetricId: AnalyticsTableMetricsEnum; // Table metric to display in the list.
  readonly innerMetricId: AnalyticsScalarMetricsEnum; // Scalar metric to display inside the donut.
  readonly innerMetricType: AnalyticsNumericDataPointType; // Metric type for the inner metric, to correctly format.
  readonly listAccessor: string; // Property accessor for the list.
  readonly color: PaletteColorsEnum; // Color scheme for the donut, list and metric picker.
  readonly metricRangeType: HistoMetricRangeTypesEnum; // Is the metric time based or using whole numbers as range.
  readonly metricBucketPickerSubtitle?: ReactNode; // Subtitle label in the bucket picker popopver.
}

export enum HistoMetricRangeTypesEnum {
  TIME = 'time',
  NUMBER = 'number',
}

/*
 * Props.
 */

interface HistoMetricSelectionDropdownProps {
  metrics: ReadonlyArray<HistoMetricSelection>;
  selectedMetric: HistoMetricSelection;
  setSelectedMetric: HandlerOf<HistoMetricSelection>;
}

/*
 * Style.
 */

const StyledDropdownButtonDiv = styled.div`
  ${flexCenter};
  gap: 5px;
`;

const StyledSimpleDropdownItem = styled(SimpleDropdownItem)`
  > div:first-child {
    ${flexCenter};
    gap: 5px;
  }
`;

/*
 * Component.
 */

export const HistoMetricSelectionDropdown: FC<HistoMetricSelectionDropdownProps> = ({
  metrics,
  selectedMetric,
  setSelectedMetric,
}) => {
  const theme = useUserTheme();

  const {isBeta, isNew, label} = getMetricLabels(selectedMetric.donutHistoMetricId);

  return (
    <FilterDropdownBox
      renderButton={() => (
        <StyledDropdownButtonDiv>
          {label}
          {maybeRenderMetricPillAndTooltip(theme, isBeta, isNew)}
        </StyledDropdownButtonDiv>
      )}
      renderDropdown={(modalProps: PopoverTransferProps) =>
        renderDropdown(modalProps, metrics, selectedMetric, setSelectedMetric, theme)
      }
    />
  );
};

/*
 * Helpers.
 */

const renderDropdown = (
  modalProps: PopoverTransferProps,
  metrics: ReadonlyArray<HistoMetricSelection>,
  selectedMetric: HistoMetricSelection,
  setSelectedMetric: HandlerOf<HistoMetricSelection>,
  theme: UserTheme,
) => (
  <SimpleDropdown {...modalProps}>
    {metrics.map((metric) => {
      const {isBeta, isNew, tooltip, label} = getMetricLabels(metric.donutHistoMetricId);
      return (
        <StyledSimpleDropdownItem
          key={metric.donutHistoMetricId}
          isChecked={selectedMetric.donutHistoMetricId === metric.donutHistoMetricId}
          onSelect={() => setSelectedMetric(metric)}
        >
          {label}
          {maybeRenderMetricPillAndTooltip(theme, isBeta, isNew, tooltip)}
        </StyledSimpleDropdownItem>
      );
    })}
  </SimpleDropdown>
);
